import React, {useCallback, useEffect, useState, memo} from "react";
import {
    Card,
    Typography,
    Tabs,
    Table,
    Tag,
    Modal,
    Grid,
    Row,
    Col,
    Button,
    Space,
    Divider,
    message,
} from "antd";
import {useParams} from "react-router-dom";
import axios from "axios";
import MathText from "../components/MathText";
import {API_BASE_URL} from "../config";

const {Title, Text} = Typography;
const {useBreakpoint} = Grid;

// ---- Consts
const STEP_TITLES = {
    1: "Reading & Writing — Module 1",
    2: "Reading & Writing — Module 2",
    3: "Math — Module 1",
    4: "Math — Module 2",
};
const isOmitted = (ans) => !ans;

// ---- Helpers
const stepOf = (rec) => Number(rec?.question?.step ?? rec?.step ?? 0);

const summarize = (list) => {
    const total = list.length;
    let correct = 0;
    let omit = 0;
    for (const it of list) {
        if (isOmitted(it.answer)) omit++;
        else if (it.isRight) correct++;
    }
    const incorrect = total - correct - omit;

    // 200–800: [20.25 + 0.6x] * 10  (x = correct)

    let score = 200
    if (total !== 0) {
        score = Math.floor(20.25 + 60 * correct / total) * 10;
    }


    return {total, correct, incorrect, omitted: omit, score};
};

// ---- Memo small render helpers
const YourAnswer = memo(function YourAnswer({ans, isRight}) {
    if (isOmitted(ans)) return <Text type="secondary">Omitted</Text>;
    return <Tag color={isRight ? "green" : "blue"}>{ans}</Tag>;
});

const StatusTag = memo(function StatusTag({ans, isRight}) {
    if (isOmitted(ans)) return <Tag color="default">Omitted</Tag>;
    return <Tag color={isRight ? "green" : "red"}>{isRight ? "Correct" : "Incorrect"}</Tag>;
});

// ---- Review modal
const ReviewModal = memo(function ReviewModal({open, onClose, item}) {
    if (!item) return null;
    const q = item.question || {};
    const your = item.answer;
    const right = item.rightAnswer;

    const variants = [
        {key: "A", text: q.variant1},
        {key: "B", text: q.variant2},
        {key: "C", text: q.variant3},
        {key: "D", text: q.variant4},
    ];

    const chip = isOmitted(your)
        ? <Tag color="default">Omitted</Tag>
        : item.isRight
            ? <Tag color="green">Correct</Tag>
            : <Tag color="red">Incorrect</Tag>;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            centered
            width={900}
            styles={{mask: {backdropFilter: "blur(6px)"}}}
            title={
                <Space wrap>
                    <Text strong>Review</Text>
                    <Tag>Step {stepOf(item)}</Tag>
                    {chip}
                </Space>
            }
        >
            <Space direction="vertical" size={12} style={{width: "100%"}}>
                {q.leftsideText && (
                    <Card size="small">
                        <MathText text={q.leftsideText}/>
                    </Card>
                )}
                <Card size="small">
                    <MathText text={q.content}/>
                </Card>

                <div>
                    <Title level={5} style={{marginBottom: 8}}>Choices</Title>
                    <Space direction="vertical" style={{width: "100%"}}>
                        {variants.map((v) => {
                            const isYour = your === v.key;
                            const isRight = right === v.key;
                            const border = isRight
                                ? "2px solid #52c41a"
                                : isYour
                                    ? "2px solid #1677ff"
                                    : "1px solid #f0f0f0";
                            const bg = isRight ? "#f6ffed" : isYour ? "#f0f7ff" : "#fff";
                            return (
                                <Card key={v.key} size="small" style={{border, background: bg, borderRadius: 8}}>
                                    <Space align="start" size={12} wrap>
                                        <Tag style={{width: 28, textAlign: "center"}}>{v.key}</Tag>
                                        <div style={{fontSize: 16}}>
                                            <MathText text={v.text || ""}/>
                                        </div>
                                    </Space>
                                </Card>
                            );
                        })}
                    </Space>
                </div>

                <Divider style={{margin: "8px 0"}}/>
                <Space size={12} wrap>
                    <Text strong>Your Answer:</Text>
                    {isOmitted(your) ? <Text type="secondary">Omitted</Text> : <Text>{your}</Text>}
                    <Divider type="vertical"/>
                    <Text strong>Correct Answer:</Text>
                    <Text>{right}</Text>
                </Space>
            </Space>
        </Modal>
    );
});

// ---- Desktop table
const StepTable = memo(
    function StepTable({data, onReview}) {
        const columns = [
            {title: "#", dataIndex: "_idx", key: "_idx", width: 64},
            // {
            //     title: "Question",
            //     dataIndex: ["question", "content"],
            //     key: "q",
            //     render: (_, rec) => (
            //         <div style={{ maxWidth: 720 }}>
            //             <MathText text={rec?.question?.content || ""} />
            //         </div>
            //     ),
            // },
            {
                title: "Your Answer",
                key: "your",
                width: 140,
                render: (_, rec) => <YourAnswer ans={rec.answer} isRight={rec.isRight}/>,
            },
            {
                title: "Correct",
                dataIndex: "rightAnswer",
                key: "right",
                width: 120,
                render: (val) => <Tag color="green">{val}</Tag>,
            },
            {
                title: "Status",
                key: "status",
                width: 140,
                render: (_, rec) => <StatusTag ans={rec.answer} isRight={rec.isRight}/>,
            },
            {
                title: "",
                key: "review",
                width: 120,
                render: (_, rec) => (
                    <Button size="small" onClick={() => onReview(rec)}>
                        Review
                    </Button>
                ),
            },
        ];

        return (
            <Table
                rowKey={(r) => r.id}
                columns={columns}
                dataSource={data}
                pagination={false}
                size="middle"
                bordered={false}
                onRow={(rec) => ({
                    onClick: () => onReview(rec),
                    style: {cursor: "pointer"},
                })}
            />
        );
    },
    (prev, next) => prev.data === next.data // referential equality
);

// ---- Mobile item
const MobileItem = memo(function MobileItem({item, index, onReview}) {
    return (
        <Card
            key={item.id}
            size="small"
            style={{marginBottom: 10, borderRadius: 10}}
            hoverable
            onClick={() => onReview(item)}
        >
            <Space align="center" style={{width: "100%", justifyContent: "space-between"}}>
                <Space>
                    <Tag>{index + 1}</Tag>
                    <Tag>{`QID: ${item.question?.id ?? "-"}`}</Tag>
                    <Tag>{`Step ${stepOf(item)}`}</Tag>
                </Space>
                <StatusTag ans={item.answer} isRight={item.isRight}/>
            </Space>

            {/*<div style={{ marginTop: 8 }}>*/}
            {/*    <MathText text={item.question?.content || ""} />*/}
            {/*</div>*/}

            <Divider style={{margin: "10px 0"}}/>

            <Row gutter={8}>
                <Col span={12}>
                    <Text type="secondary">Your</Text>
                    <div style={{marginTop: 4}}>
                        <YourAnswer ans={item.answer} isRight={item.isRight}/>
                    </div>
                </Col>
                <Col span={12}>
                    <Text type="secondary">Correct</Text>
                    <div style={{marginTop: 4}}>
                        <Tag color="green">{item.rightAnswer}</Tag>
                    </div>
                </Col>
            </Row>

            <Button block style={{marginTop: 10}} onClick={(e) => {
                e.stopPropagation();
                onReview(item);
            }}>
                Review
            </Button>
        </Card>
    );
});

// ---- Step section wrapper (memo)
const StepSection = memo(
    function StepSection({stepNo, data, isMobile, onReview}) {
        const stats = summarize(data);
        return (
            <Card
                style={{marginTop: 16, borderRadius: 12, border: '1px solid #f0f0f0'}}
                title={
                    <Space>
                        <Tag>Step {stepNo}</Tag>
                        <Text strong>{STEP_TITLES[stepNo]}</Text>
                    </Space>
                }
                extra={
                    <Space size={8} wrap>
                        <Tag>Total: {stats.total}</Tag>
                        <Tag color="green">Correct: {stats.correct}</Tag>
                        <Tag color="red">Incorrect: {stats.incorrect}</Tag>
                        <Tag>Omitted: {stats.omitted}</Tag>
                    </Space>
                }
            >
                {isMobile ? (
                    <div>
                        {data.map((item, idx) => (
                            <MobileItem key={item.id} item={item} index={idx} onReview={onReview}/>
                        ))}
                        {data.length === 0 && <Text type="secondary">No questions in this step.</Text>}
                    </div>
                ) : (
                    <StepTable data={data} onReview={onReview}/>
                )}
            </Card>
        );
    },
    (p, n) => p.data === n.data && p.isMobile === n.isMobile
);

// ---- Main page
export default function SatCollectionResult() {
    const {id} = useParams();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [loading, setLoading] = useState(false);

    // Precomputed cache — nothing recalculated on tab switch
    const [cache, setCache] = useState(() => ({
        steps: {1: [], 2: [], 3: [], 4: []},
        stats: {
            overview: {total: 0, correct: 0, incorrect: 0, omitted: 0, score: 200},
            rw: {total: 0, correct: 0, incorrect: 0, omitted: 0, score: 200},
            math: {total: 0, correct: 0, incorrect: 0, omitted: 0, score: 200},
            step: {
                1: {total: 0, correct: 0, incorrect: 0, omitted: 0, score: 200},
                2: {total: 0, correct: 0, incorrect: 0, omitted: 0, score: 200},
                3: {total: 0, correct: 0, incorrect: 0, omitted: 0, score: 200},
                4: {total: 0, correct: 0, incorrect: 0, omitted: 0, score: 200},
            },
        },
    }));


    const [activeKey, setActiveKey] = useState("OVERVIEW");

    // Review modal
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewItem, setReviewItem] = useState(null);

    const openReview = useCallback((item) => {
        setReviewItem(item);
        setReviewOpen(true);
    }, []);
    const closeReview = useCallback(() => {
        setReviewOpen(false);
        setReviewItem(null);
    }, []);

    useEffect(() => {
        let alive = true;

        async function fetchData() {
            try {
                setLoading(true);
                const token = localStorage.getItem("token");
                const {data} = await axios.get(
                    `${API_BASE_URL}/api/SatResult/by-total/${id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            Accept: "text/plain",
                        },
                    }
                );

                if (!alive) return;

                // --- Prepare once
                const steps = {1: [], 2: [], 3: [], 4: []};

                // filter only with question, sort by question.id globally, then push by step
                (data || [])
                    .filter((x) => x?.question)
                    .sort((a, b) => (a.question.id || 0) - (b.question.id || 0))
                    .forEach((rec) => {
                        const s = stepOf(rec);
                        if (steps[s]) steps[s].push(rec);
                    });

                // add per-step index once (for desktop table)
                for (const s of [1, 2, 3, 4]) {
                    steps[s] = steps[s].map((it, i) => ({...it, _idx: i + 1}));
                }

                // stats once
                const stepStats = {
                    1: summarize(steps[1]),
                    2: summarize(steps[2]),
                    3: summarize(steps[3]),
                    4: summarize(steps[4]),
                };
                const rwFlat = steps[1].concat(steps[2]);
                const mathFlat = steps[3].concat(steps[4]);
                const overview = summarize(rwFlat.concat(mathFlat))
                const math = summarize(mathFlat)
                const rw = summarize(rwFlat)
                overview.score = math.score + rw.score
                const stats = {
                    overview: overview,
                    rw: summarize(rwFlat),
                    math: summarize(mathFlat),
                    step: stepStats,
                };

                setCache({steps, stats});
            } catch (e) {
                message.error("Failed to load results");
            } finally {
                if (alive) setLoading(false);
            }
        }

        fetchData();
        return () => {
            alive = false;
        };
    }, [id]);

    const HeaderKPIoveral = ({stats}) => (
        <Row gutter={[12, 12]} style={{marginBottom: 12}}>
            <Col xs={12} md={6}>
                <Card size="small">
                    <Text type="secondary">Score</Text>
                    <Title level={3} style={{margin: 0}}>{stats.score}</Title>
                </Card>
            </Col>
            <Col xs={12} md={6}>
                <Card size="small"><Text type="secondary">Correct</Text>
                    <Title level={3} style={{margin: 0, color: "#52c41a"}}>{stats.correct}</Title>
                </Card>
            </Col>
            <Col xs={12} md={6}>
                <Card size="small"><Text type="secondary">Incorrect</Text>
                    <Title level={3} style={{margin: 0, color: "#ff4d4f"}}>{stats.incorrect}</Title>
                </Card>
            </Col>
            <Col xs={12} md={6}>
                <Card size="small"><Text type="secondary">Omitted</Text>
                    <Title level={3} style={{margin: 0, color: "rgba(0,0,0,0.45)"}}>{stats.omitted}</Title>
                </Card>
            </Col>
        </Row>
    );

    // Panes (they use ONLY cached data → no recomputation)
    const RWPane = (
        <div>
            <HeaderKPIoveral stats={cache.stats.rw}/>
            <StepSection stepNo={1} data={cache.steps[1]} isMobile={isMobile} onReview={openReview}/>
            <StepSection stepNo={2} data={cache.steps[2]} isMobile={isMobile} onReview={openReview}/>
        </div>
    );

    const MathPane = (
        <div>
            <HeaderKPIoveral stats={cache.stats.math}/>
            <StepSection stepNo={3} data={cache.steps[3]} isMobile={isMobile} onReview={openReview}/>
            <StepSection stepNo={4} data={cache.steps[4]} isMobile={isMobile} onReview={openReview}/>
        </div>
    );

    const OverviewPane = (
        <div>
            <HeaderKPIoveral stats={cache.stats.overview}/>
            <StepSection stepNo={1} data={cache.steps[1]} isMobile={isMobile} onReview={openReview}/>
            <StepSection stepNo={2} data={cache.steps[2]} isMobile={isMobile} onReview={openReview}/>
            <StepSection stepNo={3} data={cache.steps[3]} isMobile={isMobile} onReview={openReview}/>
            <StepSection stepNo={4} data={cache.steps[4]} isMobile={isMobile} onReview={openReview}/>
        </div>
    );

    return (
        <div style={{padding: screens.md ? 24 : 12}}>
            <Card
                loading={loading}
                style={{borderRadius: 12, border: '1px solid #f0f0f0'}}
                styles={{body: {padding: screens.md ? 24 : 12}}}
            >
                <Row gutter={[12, 8]} align="middle" justify="space-between" style={{marginBottom: 8}}>
                    <Col>
                        <Title level={3} style={{margin: 0}}>SAT Collection Result</Title>
                        <Text type="secondary">Total Result ID: {id}</Text>
                    </Col>
                </Row>

                <Tabs
                    activeKey={activeKey}
                    onChange={setActiveKey}
                    destroyInactiveTabPane
                    items={[
                        {
                            key: "OVERVIEW",
                            label: (
                                <Space>
                                    Overview
                                    <Tag color="blue">Score: {cache.stats.overview.score}</Tag>
                                    <Tag color="green">{cache.stats.overview.correct}</Tag>
                                    <Tag color="red">{cache.stats.overview.incorrect}</Tag>
                                </Space>
                            ),
                            children: OverviewPane,
                        },
                        {
                            key: "RW",
                            label: (
                                <Space>
                                    Reading &amp; Writing
                                    <Tag color="blue">Score: {cache.stats.rw.score}</Tag>
                                    <Tag color="green">{cache.stats.rw.correct}</Tag>
                                    <Tag color="red">{cache.stats.rw.incorrect}</Tag>
                                </Space>
                            ),
                            children:
                            RWPane,
                        },
                        {
                            key: "MATH",
                            label:
                                (
                                    <Space>
                                        Math
                                        <Tag color="blue">Score: {cache.stats.math.score}</Tag>
                                        <Tag color="green">{cache.stats.math.correct}</Tag>
                                        <Tag color="red">{cache.stats.math.incorrect}</Tag>
                                    </Space>
                                ),
                            children:
                            MathPane,
                        }
                        ,
                    ]}
                />
            </Card>

            <ReviewModal open={reviewOpen} onClose={closeReview} item={reviewItem}/>
        </div>
    )
        ;
}
